import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingFocusManagerProps,
  offset,
  Placement,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { IconButton } from '../IconButton/IconButton';
import MenuDotsIcon from './images/MenuDotsIcon.svg?react';
import { ReactNode } from 'react';
import { PopoverAnchorContainer, PopoverMenuWrapper } from './styles';

type PopoverMenuType = {
  isOpen: boolean;
  onToggleOpen: (open: boolean) => void;
  children: ReactNode;
  placement?: Placement;
  anchorSlot?: ReactNode;
  returnFocus?: FloatingFocusManagerProps['returnFocus'];
  minWidth?: string;
};

export const PopoverMenu = ({
  isOpen,
  onToggleOpen,
  children,
  placement = 'bottom-end',
  anchorSlot,
  returnFocus,
  minWidth,
}: PopoverMenuType) => {
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: onToggleOpen,
    placement,
    middleware: [offset(10), flip({ fallbackAxisSideDirection: 'end' }), shift()],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

  return (
    <>
      <PopoverAnchorContainer ref={refs.setReference} {...getReferenceProps()}>
        {anchorSlot || (
          <IconButton>
            <MenuDotsIcon />
          </IconButton>
        )}
      </PopoverAnchorContainer>
      {isOpen && (
        <FloatingFocusManager context={context} modal={false} returnFocus={returnFocus}>
          <PopoverMenuWrapper
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            $minWidth={minWidth}
          >
            {children}
          </PopoverMenuWrapper>
        </FloatingFocusManager>
      )}
    </>
  );
};
